import type { HoldingV1 } from "./schema";

export type HoldingIdentityConflictCode =
  | "duplicate_holding_id"
  | "duplicate_instrument"
  | "ambiguous_instrument";

export interface HoldingIdentityConflict {
  readonly code: HoldingIdentityConflictCode;
  readonly leftIndex: number;
  readonly rightIndex: number;
  readonly leftHoldingId: string;
  readonly rightHoldingId: string;
  readonly ticker?: string;
  readonly name?: string;
}

export interface HoldingInstrumentIdentity {
  readonly ticker?: string;
  readonly name?: string;
  /**
   * Venue is intentionally absent unless the user supplied market or exchange.
   * Missing venue makes equal tickers ambiguous rather than silently merging
   * what may be listings on different exchanges.
   */
  readonly venueKey?: string;
}

export function normalizeInstrumentTicker(value: string): string {
  return value.trim().toUpperCase();
}

export function normalizeVenue(value: string): string {
  return value.trim().toUpperCase();
}

export function normalizeInstrumentName(value: string): string {
  return value.normalize("NFKC").trim().replace(/\s+/g, " ").toLowerCase();
}

export type HoldingDisplayIdentity = Pick<
  HoldingV1,
  "ticker" | "name" | "market" | "exchange"
>;

export function isKoreanHoldingIdentity(
  holding: HoldingDisplayIdentity,
): boolean {
  const ticker = holding.ticker
    ? normalizeInstrumentTicker(holding.ticker)
    : "";
  const venue = [holding.market, holding.exchange]
    .filter((value): value is string => Boolean(value))
    .map(normalizeVenue);
  return (
    /^\d{6}(?:\.(?:KS|KQ))?$/.test(ticker) ||
    venue.some((value) =>
      value === "KR" ||
      value === "KRX" ||
      value === "KOREA" ||
      value.includes("KOSPI") ||
      value.includes("KOSDAQ")
    )
  );
}

/**
 * Compact product identity:
 * - US/global listings keep the familiar ticker shorthand.
 * - Korean listings use their instrument name because a six-digit code is
 *   poor character/UI copy. The canonical ticker remains stored separately.
 */
export function preferredHoldingDisplayName(
  holding: HoldingDisplayIdentity,
  fallback = "이름 없는 종목",
): string {
  const ticker = holding.ticker?.trim();
  const name = holding.name?.trim();
  if (isKoreanHoldingIdentity(holding)) {
    return (
      name ||
      ticker?.replace(/\.(?:KS|KQ)$/i, "") ||
      fallback
    );
  }
  return ticker || name || fallback;
}

export function getHoldingInstrumentIdentity(
  holding: Pick<HoldingV1, "ticker" | "name" | "market" | "exchange">,
): HoldingInstrumentIdentity {
  const market = holding.market
    ? normalizeVenue(holding.market)
    : undefined;
  const exchange = holding.exchange
    ? normalizeVenue(holding.exchange)
    : undefined;

  return {
    ticker: holding.ticker
      ? normalizeInstrumentTicker(holding.ticker)
      : undefined,
    name: holding.name ? normalizeInstrumentName(holding.name) : undefined,
    venueKey:
      exchange !== undefined
        ? `exchange:${exchange}`
        : market !== undefined
          ? `market:${market}`
          : undefined,
  };
}

/**
 * Detects identity collisions without mutating or coalescing holdings.
 *
 * - holdingId is always globally unique.
 * - equal ticker + equal explicit venue is a duplicate.
 * - equal ticker where either side has no venue is ambiguous.
 * - equal ticker on two different explicit venues is allowed.
 * - two name-only records with the same normalized name are ambiguous.
 */
export function findHoldingIdentityConflicts(
  holdings: readonly Pick<
    HoldingV1,
    "holdingId" | "ticker" | "name" | "market" | "exchange"
  >[],
): HoldingIdentityConflict[] {
  type IndexedHolding = {
    readonly index: number;
    readonly holding: (typeof holdings)[number];
    readonly identity: HoldingInstrumentIdentity;
  };
  type TickerBucket = {
    readonly all: IndexedHolding[];
    readonly withoutVenue: IndexedHolding[];
    readonly byVenue: Map<string, IndexedHolding[]>;
  };

  const conflicts: HoldingIdentityConflict[] = [];
  const holdingsById = new Map<string, IndexedHolding[]>();
  const holdingsByTicker = new Map<string, TickerBucket>();
  const nameOnlyHoldings = new Map<string, IndexedHolding[]>();

  const pushConflict = (
    code: HoldingIdentityConflictCode,
    left: IndexedHolding,
    right: IndexedHolding,
    detail: Pick<HoldingIdentityConflict, "ticker" | "name"> = {},
  ) => {
    conflicts.push({
      code,
      leftIndex: left.index,
      rightIndex: right.index,
      leftHoldingId: left.holding.holdingId,
      rightHoldingId: right.holding.holdingId,
      ...detail,
    });
  };

  for (let index = 0; index < holdings.length; index += 1) {
    const holding = holdings[index];
    if (!holding) {
      continue;
    }
    const identity = getHoldingInstrumentIdentity(holding);
    const current: IndexedHolding = { index, holding, identity };

    for (const previous of holdingsById.get(holding.holdingId) ?? []) {
      pushConflict("duplicate_holding_id", previous, current);
    }

    if (identity.ticker) {
      let bucket = holdingsByTicker.get(identity.ticker);
      if (!bucket) {
        bucket = {
          all: [],
          withoutVenue: [],
          byVenue: new Map(),
        };
        holdingsByTicker.set(identity.ticker, bucket);
      }

      const candidates =
        identity.venueKey === undefined
          ? bucket.all
          : [
              ...bucket.withoutVenue,
              ...(bucket.byVenue.get(identity.venueKey) ?? []),
            ];
      for (const previous of candidates) {
        // The original pairwise contract reports a duplicate holdingId once
        // and intentionally suppresses a second instrument conflict.
        if (previous.holding.holdingId === holding.holdingId) {
          continue;
        }
        pushConflict(
          previous.identity.venueKey === undefined ||
            identity.venueKey === undefined
            ? "ambiguous_instrument"
            : "duplicate_instrument",
          previous,
          current,
          { ticker: identity.ticker },
        );
      }

      bucket.all.push(current);
      if (identity.venueKey === undefined) {
        bucket.withoutVenue.push(current);
      } else {
        const venueHoldings = bucket.byVenue.get(identity.venueKey) ?? [];
        venueHoldings.push(current);
        bucket.byVenue.set(identity.venueKey, venueHoldings);
      }
    } else if (identity.name) {
      const previousNameOnly = nameOnlyHoldings.get(identity.name) ?? [];
      for (const previous of previousNameOnly) {
        if (previous.holding.holdingId !== holding.holdingId) {
          pushConflict(
            "ambiguous_instrument",
            previous,
            current,
            { name: identity.name },
          );
        }
      }
      previousNameOnly.push(current);
      nameOnlyHoldings.set(identity.name, previousNameOnly);
    }

    const sameId = holdingsById.get(holding.holdingId) ?? [];
    sameId.push(current);
    holdingsById.set(holding.holdingId, sameId);
  }

  // Preserve the old nested-loop diagnostic ordering for stable error paths.
  return conflicts.sort(
    (left, right) =>
      left.leftIndex - right.leftIndex ||
      left.rightIndex - right.rightIndex,
  );
}
